import { ampFetch } from './api';
import { has } from 'lodash';
import { isLoggedIn } from '~/account/utilities/helpers';

/**
 * @typedef SubscriptionResponsePlan
 * @property {string} [name]
 * @property {number} product_id
 * @property {number} price_id
 * @property {number} quantity
 */

/**
 * @typedef SubscriptionResponse
 * @property {string} stripe_subscription_id
 * @property {string} [stripe_customer_id]
 * @property {string} [status]
 * @property {boolean} is_cancel_at_period_end datetime
 * @property {string} current_period_end datetime
 * @property {string} [downgrade_expiration_date] datetime
 * @property {string} [pause_request_date] datetime
 * @property {string} [pause_effective_date] datetime
 * @property {string} [location_id]
 * @property {string} created_date datetime
 * @property {string} modified_date datetime
 * @property {SubscriptionResponsePlan} plan
 */

const updateItemWithInvoice = (invoice, item, refreshKey) => {
    let discount = 0;

    if (invoice.discount) {
        discount = invoice.discount;
    } else if (invoice.total < invoice.subtotal) {
        discount = invoice.subtotal + invoice.tax - invoice.total;
    }

    return {
        ...item,
        invoice: {
            ...invoice,
            discount
        },
        discount,
        refreshKey,
        invoice_id: invoice.stripe_subscription_id,
        tax: invoice.tax,
        price: invoice.subtotal
    };
};

const subscriptions = {
    /**
     * @param vehicle_id
     * @param price_id
     * @param payment_method_id
     * @param promo_code_id
     * @param referral_code
     * @param closest_location_external_id
     * @param latitude
     * @param longitude
     * @param pos_plan_id
     * @returns {Promise<SubscriptionResponse>}
     */
    createSubscription: async (
        vehicle_id,
        price_id,
        payment_method_id,
        promo_code_id,
        referral_code,
        closest_location_external_id,
        latitude,
        longitude,
        pos_plan_id
    ) => {
        return await ampFetch({
            route: 'api-user/subscription/create-vehicle-subscription',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    vehicle_id,
                    price_id,
                    payment_method_id,
                    promo_code_id,
                    referral_code,
                    closest_location_external_id,
                    latitude,
                    longitude,
                    pos_plan_id
                })
            }
        });
    },
    previewNewSubscriptionInvoice: async (
        item,
        closest_location_external_id
    ) => {
        if (!isLoggedIn()) {
            return { success: false, subscriptionItem: item };
        }

        const subscription_plan_id = item.subscriptionPlanId;
        const billing_frequency_id = item.billingFrequencyId;
        const location_id = closest_location_external_id;
        const promo_code = item.promoCode?.code;
        const refreshKey = `${subscription_plan_id}-${location_id}-${billing_frequency_id}-${promo_code}`;

        if (item.refreshKey === refreshKey && has(item, 'invoice')) {
            return { success: true, subscriptionItem: item };
        }

        const response = await ampFetch({
            route: 'api-user/subscription/preview-new-subscription-invoice',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    subscription_plan_id,
                    billing_frequency_id,
                    pricing_group_id: item.pricingGroupId,
                    price: item.price,
                    location_id,
                    ...(promo_code && { promo_code })
                })
            }
        });

        if (response.success) {
            const subscriptionItem = updateItemWithInvoice(
                response.data.invoice,
                item,
                refreshKey
            );
            return { success: true, subscriptionItem };
        }

        return { success: false, subscriptionItem: item };
    },
    /**
     *
     * @param id
     * @param subscription_plan_id
     * @param billing_frequency_id
     * @returns {Promise<SubscriptionResponse>}
     */
    updateSubscription: async (
        id,
        subscription_plan_id,
        billing_frequency_id,
        vehicle_id
    ) => {
        return await ampFetch({
            route: 'api-user/subscription/update-vehicle-subscription',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    id,
                    subscription_plan_id,
                    billing_frequency_id,
                    vehicle_id
                })
            }
        });
    },
    validateSubscriptionPromoCode: async (
        price_id,
        code_name,
        sub_total,
        phone_number,
        subscription_id
    ) => {
        return await ampFetch({
            route: 'api-user/subscription/validate-subscription-promotion-code',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    price_id,
                    code_name,
                    sub_total,
                    phone_number,
                    subscription_id
                })
            }
        });
    },
    cancelSubscription: async (subscription_id, reason) => {
        return await ampFetch({
            route: 'api-user/subscription/cancel-subscription',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    subscription_id,
                    reason
                })
            }
        });
    }
};

export default subscriptions;
